import React, { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, User, Mail, Phone, Calendar, CreditCard, MessageSquare, Trash2, AlertTriangle, Pencil, Camera, FileText, Wallet, ChevronDown, ChevronUp, CheckCircle, XCircle, Clock, Download, ExternalLink, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { BookingDetailModal } from './BookingDetailModal';

const statusLabels = {
  confirmed: { l: 'Confirmé', c: 'bg-[#00FF00]/15 text-[#00FF00]' },
  pending: { l: 'En attente', c: 'bg-yellow-500/15 text-yellow-400' },
  pending_onsite: { l: 'Sur place', c: 'bg-blue-500/15 text-blue-400' },
  pending_payment_link: { l: 'Lien envoyé', c: 'bg-purple-500/15 text-purple-400' },
  cancelled: { l: 'Annulé', c: 'bg-red-500/15 text-red-400' },
};

const paymentDetailLabels = {
  cb: 'CB', especes: 'Espèces', cheques_vacances: 'Chq. Vacances', credits: 'Crédits', autres: 'Autres', online: 'Carte en ligne', onsite: 'Sur place', gift_card: 'Carte cadeau', admin: 'Admin', pay_by_link: 'Lien paiement',
};

const emailTypeLabels = {
  booking_confirmation: 'Confirmation',
  booking_cancellation: 'Annulation',
  admin_new_booking: 'Notif admin',
  review_request: 'Avis',
  flash_promo: 'Promo flash',
  raw: 'Email direct',
};

const gmailComposeUrl = (to, subject) =>
  `https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(to)}&su=${encodeURIComponent(subject)}`;

const API_BASE = process.env.REACT_APP_BACKEND_URL;

function Section({ title, icon: Icon, count, children, defaultOpen = false, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[#0A0A0A] rounded-xl border border-[#2A2A2A] overflow-hidden" data-testid={testId}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-3 hover:bg-[#111] transition-colors">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#00FF00]" />
          <span className="text-xs font-semibold text-slate-300 uppercase tracking-wider">{title}</span>
          {count !== undefined && <Badge className="bg-[#00FF00]/10 text-[#00FF00] border-0 text-[10px] ml-1">{count}</Badge>}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
      </button>
      {open && <div className="px-3 pb-3 border-t border-[#1A1A1A]">{children}</div>}
    </div>
  );
}

export function ClientDetailModal({ clientId, open, onClose, onDeleted, activities = [] }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);

  const loadClient = () => {
    if (!clientId) return;
    setLoading(true);
    adminAPI.getClientDetail(clientId)
      .then(r => setData(r.data))
      .catch(() => toast.error('Impossible de charger le client'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || !clientId) { setData(null); return; }
    loadClient();
  }, [clientId, open]);

  const handleDeleteClient = async () => {
    setDeleting(true);
    try {
      await adminAPI.deleteClient(clientId);
      toast.success('Client supprimé, réservations anonymisées');
      setShowDeleteConfirm(false);
      onClose();
      if (onDeleted) onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur lors de la suppression');
    } finally { setDeleting(false); }
  };

  const handleConfirmPayment = async (bookingId, paymentData) => {
    await adminAPI.confirmOnsitePayment(bookingId, paymentData);
    toast.success('Paiement confirmé');
    loadClient();
  };

  const client = data?.client;
  const bookings = data?.bookings || [];
  const stats = data?.stats || {};
  const payments = data?.payments || [];
  const creditBalances = data?.credit_balances || [];
  const creditTransactions = data?.credit_transactions || [];
  const contracts = data?.contracts || [];
  const emailLogs = data?.email_logs || [];
  const notifLogs = data?.notification_logs || [];
  const allEmails = [...emailLogs, ...notifLogs.map(n => ({
    ...n, to_email: client?.email, subject: n.message?.slice(0, 60) || 'Notification',
    email_type: 'notification', success: n.results?.email_sent || n.results?.push_sent > 0,
    sent_at: n.sent_at,
  }))].sort((a, b) => (b.sent_at || '').localeCompare(a.sent_at || ''));

  const totalCredits = creditBalances.reduce((s, c) => s + (c.balance || 0), 0);

  return (
    <>
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="bg-[#1A1A1A] border-[#2A2A2A] text-white sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="client-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg">Fiche Client</DialogTitle>
          <DialogDescription className="sr-only">Profil complet du client</DialogDescription>
        </DialogHeader>

        {loading && <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#00FF00]" /></div>}

        {!loading && client && (
          <div className="space-y-3 mt-2">
            {/* Client info header */}
            <div className="bg-[#0A0A0A] rounded-xl p-4 border border-[#2A2A2A]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-[#00FF00]/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-[#00FF00]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-white" data-testid="client-name">{client.name}</h3>
                  <p className="text-xs text-slate-400">{client.email}</p>
                  {client.phone && <p className="text-xs text-slate-500">{client.phone}</p>}
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="bg-[#1A1A1A] rounded-lg p-2 text-center">
                  <p className="text-base font-bold text-[#00FF00]">{stats.total_bookings}</p>
                  <p className="text-[9px] text-slate-500">Résa</p>
                </div>
                <div className="bg-[#1A1A1A] rounded-lg p-2 text-center">
                  <p className="text-base font-bold text-[#00FF00]">{stats.total_spent}€</p>
                  <p className="text-[9px] text-slate-500">Dépensé</p>
                </div>
                <div className="bg-[#1A1A1A] rounded-lg p-2 text-center">
                  <p className="text-base font-bold text-[#00FF00]">{stats.confirmed_count}</p>
                  <p className="text-[9px] text-slate-500">Confirmées</p>
                </div>
                <div className="bg-[#1A1A1A] rounded-lg p-2 text-center">
                  <p className={`text-base font-bold ${totalCredits > 0 ? 'text-amber-400' : 'text-slate-600'}`}>{totalCredits}€</p>
                  <p className="text-[9px] text-slate-500">Crédits</p>
                </div>
              </div>
              {/* Quick contact */}
              <div className="flex flex-wrap gap-2 pt-3 border-t border-[#2A2A2A]">
                {client.phone && (
                  <>
                    <a href={`tel:${client.phone}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/30 text-blue-400 text-xs font-medium hover:bg-blue-500/20" data-testid="client-call">
                      <Phone className="w-3 h-3" /> Appeler
                    </a>
                    <a href={`sms:${client.phone}`} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-purple-500/10 border border-purple-500/30 text-purple-400 text-xs font-medium hover:bg-purple-500/20" data-testid="client-sms">
                      <MessageSquare className="w-3 h-3" /> SMS
                    </a>
                  </>
                )}
                <a href={gmailComposeUrl(client.email, `Riding Watt - ${client.name}`)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-400 text-xs font-medium hover:bg-amber-500/20" data-testid="client-email">
                  <Mail className="w-3 h-3" /> Email
                </a>
              </div>
            </div>

            {/* BOOKINGS */}
            <Section title="Réservations" icon={Calendar} count={bookings.length} defaultOpen={true} testId="section-bookings">
              <div className="space-y-2 max-h-48 overflow-y-auto mt-2">
                {bookings.map(b => {
                  const st = statusLabels[b.status] || statusLabels.pending;
                  return (
                    <div key={b.id} className="bg-[#1A1A1A] rounded-lg p-2.5 border border-[#2A2A2A] hover:border-[#444] transition-colors cursor-pointer group" data-testid={`client-booking-${b.id}`} onClick={() => setSelectedBookingId(b.id)}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs font-semibold text-white">{b.activity_name}</span>
                        <div className="flex items-center gap-1.5">
                          <Badge className={`${st.c} border-0 text-[9px]`}>{st.l}</Badge>
                          <Pencil className="w-3 h-3 text-slate-600 group-hover:text-[#00FF00] transition-colors" />
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-slate-400">
                        <span>{b.date}</span>
                        <span>{b.time_slot}</span>
                        <span className="font-medium text-white">{b.price}€</span>
                        {b.payment_detail_method && <Badge className="bg-[#00FF00]/10 text-[#00FF00] border-0 text-[8px]">{paymentDetailLabels[b.payment_detail_method] || b.payment_detail_method}</Badge>}
                        {b.photo_count > 0 && <span className="text-[#00FF00]"><Camera className="w-3 h-3 inline" /> {b.photo_count}</span>}
                      </div>
                    </div>
                  );
                })}
                {bookings.length === 0 && <p className="text-xs text-slate-500 text-center py-3">Aucune réservation</p>}
              </div>
            </Section>

            {/* PAYMENTS */}
            <Section title="Paiements" icon={CreditCard} count={payments.length} testId="section-payments">
              <div className="space-y-1.5 max-h-48 overflow-y-auto mt-2">
                {payments.map(p => (
                  <div key={p.id} className="bg-[#1A1A1A] rounded-lg p-2.5 border border-[#2A2A2A]" data-testid={`payment-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-white">{p.amount}€</span>
                        <Badge className={`border-0 text-[9px] ${p.payment_status === 'paid' ? 'bg-[#00FF00]/15 text-[#00FF00]' : 'bg-yellow-500/15 text-yellow-400'}`}>
                          {p.payment_status === 'paid' ? 'Payé' : 'En attente'}
                        </Badge>
                      </div>
                      <span className="text-[10px] text-slate-500">
                        {p.created_at ? new Date(p.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' }) : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-400">
                      <span>{paymentDetailLabels[p.metadata?.payment_method] || paymentDetailLabels[p.metadata?.payment_detail_method] || p.metadata?.payment_method || '—'}</span>
                      {p.metadata?.payment_detail_method && <Badge className="bg-slate-700 text-slate-300 border-0 text-[8px]">{paymentDetailLabels[p.metadata.payment_detail_method]}</Badge>}
                      {p.metadata?.type === 'credit_purchase' && <Badge className="bg-amber-500/15 text-amber-400 border-0 text-[8px]">Achat crédits</Badge>}
                    </div>
                  </div>
                ))}
                {payments.length === 0 && <p className="text-xs text-slate-500 text-center py-3">Aucun paiement</p>}
              </div>
            </Section>

            {/* CREDITS */}
            <Section title="Crédits" icon={Wallet} count={totalCredits > 0 ? `${totalCredits}€` : '0€'} testId="section-credits">
              <div className="mt-2 space-y-2">
                {creditBalances.length > 0 && (
                  <div className="grid grid-cols-2 gap-2">
                    {creditBalances.map(cb => (
                      <div key={cb.site_id} className="bg-[#1A1A1A] rounded-lg p-2 border border-[#2A2A2A] text-center">
                        <p className="text-lg font-bold text-amber-400">{cb.balance}€</p>
                        <p className="text-[9px] text-slate-500">{cb.site_id}</p>
                      </div>
                    ))}
                  </div>
                )}
                {creditTransactions.length > 0 && (
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    <p className="text-[10px] text-slate-500 uppercase tracking-wider">Historique</p>
                    {creditTransactions.map(t => (
                      <div key={t.id} className="flex items-center justify-between bg-[#1A1A1A] rounded px-2 py-1.5 border border-[#2A2A2A]" data-testid={`credit-tx-${t.id}`}>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs font-bold ${t.amount >= 0 ? 'text-[#00FF00]' : 'text-red-400'}`}>{t.amount >= 0 ? '+' : ''}{t.amount}€</span>
                          <span className="text-[10px] text-slate-400 truncate max-w-[140px]">{t.description}</span>
                        </div>
                        <span className="text-[9px] text-slate-600">{t.created_at ? new Date(t.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : ''}</span>
                      </div>
                    ))}
                  </div>
                )}
                {creditBalances.length === 0 && <p className="text-xs text-slate-500 text-center py-3">Aucun crédit</p>}
              </div>
            </Section>

            {/* CONTRACTS */}
            <Section title="Contrats signés" icon={FileText} count={contracts.length} testId="section-contracts">
              <div className="space-y-1.5 max-h-40 overflow-y-auto mt-2">
                {contracts.map(c => (
                  <div key={c.id} className="bg-[#1A1A1A] rounded-lg p-2.5 border border-[#2A2A2A] flex items-center justify-between" data-testid={`contract-${c.id}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge className={`border-0 text-[9px] ${c.status === 'signed' ? 'bg-[#00FF00]/15 text-[#00FF00]' : 'bg-orange-500/15 text-orange-400'}`}>
                          {c.status === 'signed' ? 'Signé' : c.status}
                        </Badge>
                        <span className="text-xs text-white font-medium">{c.activity_name || ''}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-400">
                        {c.booking_date && <span>{c.booking_date}</span>}
                        {c.signed_at && <span>Signé le {new Date(c.signed_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' })}</span>}
                      </div>
                    </div>
                    {c.pdf_path && c.booking_id && (
                      <a href={adminAPI.getBookingContractPdf(c.booking_id)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-1 rounded bg-[#00FF00]/10 text-[#00FF00] text-[10px] font-medium hover:bg-[#00FF00]/20" data-testid={`view-pdf-${c.id}`}>
                        <Download className="w-3 h-3" /> PDF
                      </a>
                    )}
                  </div>
                ))}
                {contracts.length === 0 && <p className="text-xs text-slate-500 text-center py-3">Aucun contrat</p>}
              </div>
            </Section>

            {/* EMAILS */}
            <Section title="Emails envoyés" icon={Mail} count={allEmails.length} testId="section-emails">
              <div className="space-y-1.5 max-h-48 overflow-y-auto mt-2">
                {allEmails.map((e, i) => (
                  <div key={e.id || i} className="bg-[#1A1A1A] rounded-lg p-2.5 border border-[#2A2A2A]" data-testid={`email-log-${e.id || i}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {e.success ? <CheckCircle className="w-3 h-3 text-[#00FF00]" /> : <XCircle className="w-3 h-3 text-red-400" />}
                        <Badge className="bg-[#2A2A2A] text-slate-300 border-0 text-[9px]">
                          {emailTypeLabels[e.email_type] || e.email_type || 'Email'}
                        </Badge>
                      </div>
                      <span className="text-[9px] text-slate-500">
                        {e.sent_at ? new Date(e.sent_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 mt-1 truncate">{e.subject || e.message || '—'}</p>
                  </div>
                ))}
                {allEmails.length === 0 && <p className="text-xs text-slate-500 text-center py-3">Aucun email envoyé</p>}
              </div>
            </Section>

            {/* Delete Client */}
            <div className="bg-[#0A0A0A] rounded-xl p-3 border border-red-500/20">
              {!showDeleteConfirm ? (
                <Button onClick={() => setShowDeleteConfirm(true)} variant="outline" className="w-full border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 bg-transparent text-xs" data-testid="delete-client-btn">
                  <Trash2 className="w-3 h-3 mr-2" /> Supprimer ce client
                </Button>
              ) : (
                <div className="space-y-2" data-testid="delete-client-confirm">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-xs font-medium text-red-400">Confirmer la suppression</p>
                      <p className="text-[10px] text-slate-500 mt-1">Compte supprimé, réservations anonymisées.</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleDeleteClient} disabled={deleting} className="flex-1 bg-red-500 text-white hover:bg-red-600 font-bold text-xs h-8" data-testid="confirm-delete-client-btn">
                      {deleting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />} Supprimer
                    </Button>
                    <Button onClick={() => setShowDeleteConfirm(false)} variant="outline" className="border-[#2A2A2A] text-slate-400 bg-transparent text-xs h-8">Annuler</Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    <BookingDetailModal
      bookingId={selectedBookingId}
      open={!!selectedBookingId}
      onClose={() => { setSelectedBookingId(null); loadClient(); }}
      onConfirmPayment={handleConfirmPayment}
      API_BASE={API_BASE}
      activities={activities}
      onRefresh={loadClient}
    />
    </>
  );
}
